"use client";

import { useEffect, useRef, type MouseEvent, type TouchEvent } from "react";

const LONG_PRESS_MS = 500;
const MOVE_TOLERANCE_PX = 10;
const CLICK_SUPPRESS_MS = 400;

function isInside(event: MouseEvent<HTMLElement>) {
  return event.target instanceof Node && event.currentTarget.contains(event.target);
}

function hasMoved(start: { x: number; y: number }, touch: { clientX: number; clientY: number }) {
  return (
    Math.abs(touch.clientX - start.x) > MOVE_TOLERANCE_PX ||
    Math.abs(touch.clientY - start.y) > MOVE_TOLERANCE_PX
  );
}

export function useLongPress(onLongPress: (() => void) | undefined) {
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const origin = useRef<{ x: number; y: number } | null>(null);
  const fired = useRef(false);
  const releasedAt = useRef<number | null>(null);

  const cancel = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
  };

  useEffect(() => cancel, []);

  if (!onLongPress) return {};

  return {
    onTouchStart: (event: TouchEvent<HTMLElement>) => {
      const touch = event.touches[0];
      if (!touch) return;
      origin.current = { x: touch.clientX, y: touch.clientY };
      fired.current = false;
      releasedAt.current = null;
      cancel();
      timer.current = setTimeout(() => {
        fired.current = true;
        onLongPress();
      }, LONG_PRESS_MS);
    },
    onTouchMove: (event: TouchEvent<HTMLElement>) => {
      const touch = event.touches[0];
      if (touch && origin.current && hasMoved(origin.current, touch)) cancel();
    },
    onTouchEnd: () => {
      cancel();
      if (fired.current) releasedAt.current = Date.now();
    },
    onTouchCancel: cancel,
    onClickCapture: (event: MouseEvent<HTMLElement>) => {
      if (!fired.current || !isInside(event)) return;
      const released = releasedAt.current;
      fired.current = false;
      releasedAt.current = null;
      if (released === null || Date.now() - released > CLICK_SUPPRESS_MS) return;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
